import { t } from "i18next";
import { AthletePerformance } from "../../interfaces/athlete";
import moment from 'moment';
import ReactECharts from 'echarts-for-react';
import { LoadingSpinner } from "../general/loading-spinner";
import { useKpiSettings } from "../../contexts/KpiSettingsContext";
import { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { formatKpiValue } from "../../util/time-format";

interface AthletePerformanceChartProps {
    performance: AthletePerformance[];
}

const chartColors = [
    '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
    '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#5470c6'
];

export function AthletePerformanceChart(props: AthletePerformanceChartProps) {
    const { getActiveKpis, getKpiLabelWithUnit, kpiSettingsLoaded, getKpiByName } = useKpiSettings();
    const [selectedKpiName, setSelectedKpiName] = useState<string>('');
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

    // Initialize with first active KPI
    useEffect(() => {
        if (kpiSettingsLoaded) {
            const activeKpis = getActiveKpis();
            if (activeKpis.length > 0 && !selectedKpiName) {
                setSelectedKpiName(activeKpis[0].kpi_name);
            }
        }
    }, [kpiSettingsLoaded]);

    if (!kpiSettingsLoaded) {
        return (
            <div className="athlete-performance-chart-container">
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    const activeKpis = getActiveKpis();

    if (activeKpis.length === 0) {
        return (
            <div className="athlete-performance-chart-container">
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p className="text-muted">{t("no_active_kpis_configured")}</p>
                </div>
            </div>
        );
    }

    // Filter by selected year and sort by date
    const filteredPerformance = props.performance.filter(item => {
        const itemYear = new Date(item.performance_date).getFullYear();
        return itemYear === selectedYear;
    });

    const sortedPerformance = [...filteredPerformance].sort((a, b) =>
        new Date(a.performance_date).getTime() - new Date(b.performance_date).getTime()
    );

    // Get available years from performance data
    const availableYears = Array.from(new Set(
        props.performance.map(item => new Date(item.performance_date).getFullYear())
    )).sort((a, b) => b - a); // Sort descending (newest first)

    if (sortedPerformance.length === 0) {
        return (
            <div className="athlete-performance-chart-container">
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p className="text-muted">{t("No performance data available")}</p>
                </div>
            </div>
        );
    }

    // Get selected KPI details
    const selectedKpi = getKpiByName(selectedKpiName);
    if (!selectedKpi) {
        return (
            <div className="athlete-performance-chart-container">
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p className="text-muted">{t("Select a KPI to display")}</p>
                </div>
            </div>
        );
    }

    // Extraer datos para el gráfico
    const dates = sortedPerformance.map(item => moment(item.performance_date).format('DD/MM/YYYY'));

    // Build series for selected KPI only
    const dataValues = sortedPerformance.map(item => {
        const value = item.kpis[selectedKpi.kpi_name];
        return value !== undefined && value !== null ? value : null;
    });

    // Calculate statistics (excluding null values)
    const validValues = dataValues.filter(v => v !== null) as number[];
    const maxValue = validValues.length > 0 ? Math.max(...validValues) : 0;
    const minValue = validValues.length > 0 ? Math.min(...validValues) : 0;
    const avgValue = validValues.length > 0 ? validValues.reduce((a, b) => a + b, 0) / validValues.length : 0;

    const series = [{
        name: getKpiLabelWithUnit(selectedKpi.kpi_name),
        type: 'line',
        data: dataValues,
        smooth: true,
        itemStyle: {
            color: chartColors[0]
        },
        emphasis: {
            focus: 'series'
        },
        connectNulls: false, // Don't connect points with null values
        markLine: {
            symbol: 'none',
            label: {
                position: 'end',
                formatter: (params: any) => {
                    // Get the actual yAxis value
                    const value = params.data.yAxis;
                    let formattedValue: string;

                    // Format based on KPI type
                    if (selectedKpi.data_type === 'time') {
                        formattedValue = formatKpiValue(value, 'time');
                    } else {
                        formattedValue = value.toFixed(2);
                    }

                    return `${params.name}: ${formattedValue}`;
                }
            },
            data: [
                {
                    name: t('Maximum'),
                    yAxis: maxValue,
                    lineStyle: {
                        color: '#ee6666',
                        type: 'dashed',
                        width: 2
                    }
                },
                {
                    name: t('Average'),
                    yAxis: avgValue,
                    lineStyle: {
                        color: '#fac858',
                        type: 'dashed',
                        width: 2
                    }
                },
                {
                    name: t('Minimum'),
                    yAxis: minValue,
                    lineStyle: {
                        color: '#91cc75',
                        type: 'dashed',
                        width: 2
                    }
                }
            ]
        }
    }];

    const option = {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross'
            },
            formatter: (params: any) => {
                if (!params || params.length === 0) return '';
                const param = params[0];
                const value = param.value;
                const formattedValue = selectedKpi.data_type === 'time'
                    ? formatKpiValue(value, 'time')
                    : formatKpiValue(value, 'decimal');
                return `${param.axisValue}<br/>${param.marker}${param.seriesName}: ${formattedValue}`;
            }
        },
        grid: {
            top: '15%',
            left: '3%',
            right: '4%',
            bottom: '10%',
            containLabel: true
        },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: dates,
            axisLabel: {
                rotate: 45
            }
        },
        yAxis: {
            type: 'value',
            name: selectedKpi.unit || getKpiLabelWithUnit(selectedKpi.kpi_name),
            axisLabel: {
                formatter: (value: number) => {
                    if (selectedKpi.data_type === 'time') {
                        return formatKpiValue(value, 'time');
                    }
                    return value.toFixed(2);
                }
            }
        },
        series: series
    };

    return (
        <div className="athlete-performance-chart-container">
            <div className="mb-3" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <Form.Select
                    value={selectedKpiName}
                    onChange={(e) => setSelectedKpiName(e.target.value)}
                    style={{ width: 'auto', minWidth: '200px' }}
                >
                    {activeKpis.map(kpi => (
                        <option key={kpi.kpi_name} value={kpi.kpi_name}>
                            {getKpiLabelWithUnit(kpi.kpi_name)}
                        </option>
                    ))}
                </Form.Select>
                <Form.Select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    style={{ width: 'auto', minWidth: '120px' }}
                >
                    {availableYears.map(year => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </Form.Select>
            </div>
            <div className="chart-content">
                <ReactECharts option={option} style={{ height: '300px' }} />
            </div>
        </div>
    );
}
